import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FAMILY_PACKAGE_PERSON_COUNT } from 'components/global/Family/constants'

import {
    Person,
    Ticket,
    Zone,
    TicketBox,
    Time,
    TicketBoxItem,
} from 'store/api-slice/types'
import { v4 as uuidv4 } from 'uuid'

export interface setFamilyPackageSelectionInterface {
    isSelected: boolean
    shouldKeepTickets?: boolean
    willAvoidAction?: boolean
    peopleCount?: number
}

interface TicketState {
    isMultipleDays: boolean
    dates: string[]
    isCalendarOpened: boolean
    people: number
    areTicketsVisible: boolean
    ticketBoxes: TicketBox[]
    areTicketsUpdating: boolean
    familyPackage: {
        isSelected: boolean
        isVisible: boolean
        isConflictModalOpened: boolean
    }
    seniorPackage: {
        isConflictModalOpened: boolean
    }
}

const emptyTicketBox: TicketBox = {
    name: '',
    types: [],
    date: '',
    zone: '' as Zone,
    person: '' as Person,
    ticketTime: '' as Time,
    boxId: '',
    isBundle: false,
}

const initialState: TicketState = {
    isMultipleDays: false,
    dates: [],
    isCalendarOpened: false,
    people: 1,
    areTicketsVisible: false,
    ticketBoxes: [
        {
            ...emptyTicketBox,
            boxId: uuidv4(),
        },
    ],
    areTicketsUpdating: false,
    familyPackage: {
        isSelected: false,
        isVisible: false,
        isConflictModalOpened: false,
    },
    seniorPackage: {
        isConflictModalOpened: false,
    },
}

const ticketSlice = createSlice({
    name: 'ticket',
    initialState,
    reducers: {
        setMultipleDays(state, action: PayloadAction<boolean>) {
            state.isMultipleDays = action.payload
        },
        setDates(state, action: PayloadAction<string[]>) {
            state.dates = action.payload
        },
        setIsCalendarOpened(state, action: PayloadAction<boolean>) {
            state.isCalendarOpened = action.payload
        },
        removeDates(state) {
            state.dates = initialState.dates
        },
        addPerson(state, action: PayloadAction<string | undefined>) {
            const boxId = action?.payload || uuidv4()

            state.people += 1
            state.ticketBoxes.push({
                ...emptyTicketBox,
                boxId,
            })
        },
        removePerson(state) {
            if (state.people > 1) {
                state.people = state.people - 1
            }
            state.ticketBoxes.splice(-1)
        },
        setTicketsVisible(state, action: PayloadAction<boolean>) {
            state.areTicketsVisible = action.payload
        },
        setTicketsUpdating(state, action: PayloadAction<boolean>) {
            state.areTicketsUpdating = action.payload
        },
        addTicket(
            state,
            action: PayloadAction<{
                data: Ticket
                boxId: string
            }>
        ) {
            const { data, boxId } = action.payload

            const boxIndex =
                state.ticketBoxes.findIndex((box) => box.boxId === boxId) || 0

            const {
                detail,
                cartItemId,
                cartItemBundleId,
                description,
                name,
                productId,
                quantity,
                totalPrice,
                unitPrice,
            } = data

            const { date, person, ticketTime, zone } = detail
            const newType: TicketBoxItem = {
                cartItemId,
                price: unitPrice,
                productId,
                quantity,
            }

            if (!state.ticketBoxes[boxIndex]) return
            state.ticketBoxes[boxIndex] = {
                date,
                types: [...state.ticketBoxes[boxIndex].types, newType],
                boxId,
                description,
                name,
                person,
                price: totalPrice,
                ticketTime,
                zone,
                isBundle: !!cartItemBundleId,
            }
        },
        removeTicketTypes(state, action: PayloadAction<string>) {
            const boxId = action.payload

            const boxIndex =
                state.ticketBoxes.findIndex((box) => box.boxId === boxId) || 0

            if (!state.ticketBoxes[boxIndex]) return
            state.ticketBoxes[boxIndex].types = []
        },
        removeTicket(state, action: PayloadAction<string>) {
            const ticketIndexToRemove = state.ticketBoxes.findIndex(
                (ticket) => ticket.boxId === action.payload
            )

            state.ticketBoxes.splice(ticketIndexToRemove, 1)

            if (state.ticketBoxes.length === 0) {
                if (!state.familyPackage) {
                    state.areTicketsVisible = false
                    state.ticketBoxes.push({
                        ...emptyTicketBox,
                        boxId: uuidv4(),
                    })
                }
                state.people -= 1
            } else {
                state.people = state.people - 1
            }
        },
        removeAllTickets(state) {
            state.isMultipleDays = false
            state.dates = initialState.dates
            state.people = 1
            state.ticketBoxes = [
                {
                    ...emptyTicketBox,
                    boxId: uuidv4(),
                },
            ]
            state.areTicketsVisible = false
        },
        updateTickets(state, action: PayloadAction<{ people: number }>) {
            state.people = action.payload.people

            const ticketBoxes = Array.from({
                length: action.payload.people,
            }).map(() => ({
                ...emptyTicketBox,
                boxId: uuidv4(),
            }))

            state.ticketBoxes = ticketBoxes
        },
        updateTicketPersonType(
            state,
            action: PayloadAction<{
                person: Person
                boxId: string
            }>
        ) {
            const { person, boxId } = action.payload

            const boxIndex = state.ticketBoxes.findIndex(
                (box) => box.boxId === boxId
            )

            state.ticketBoxes[boxIndex].person = person

            const { zone, ticketTime } = state.ticketBoxes[boxIndex]

            if (zone) {
                state.ticketBoxes[boxIndex].zone = '' as Zone
            }

            if (ticketTime) {
                state.ticketBoxes[boxIndex].ticketTime = '' as Time
            }
        },
        updateTicketZoneType(
            state,
            action: PayloadAction<{
                zone: Zone
                boxId: string
            }>
        ) {
            const { zone, boxId } = action.payload

            const boxIndex = state.ticketBoxes.findIndex(
                (box) => box.boxId === boxId
            )

            state.ticketBoxes[boxIndex].zone = zone

            const { ticketTime } = state.ticketBoxes[boxIndex]

            if (ticketTime) {
                state.ticketBoxes[boxIndex].ticketTime = '' as Time
            }
        },
        removeTicketsKeepDate(state) {
            state.ticketBoxes = [
                {
                    ...emptyTicketBox,
                    boxId: uuidv4(),
                },
            ]
            state.areTicketsVisible = false
        },
        setFamilyPackageSelection(
            state,
            action: PayloadAction<setFamilyPackageSelectionInterface>
        ) {
            const {
                isSelected,
                shouldKeepTickets,
                willAvoidAction,
                peopleCount,
            } = action.payload

            if (!willAvoidAction) {
                if (isSelected) {
                    if (shouldKeepTickets) {
                        if (peopleCount) {
                            state.people = peopleCount
                        } else {
                            state.people = FAMILY_PACKAGE_PERSON_COUNT
                        }
                    } else {
                        if (peopleCount) {
                            const diff =
                                peopleCount - FAMILY_PACKAGE_PERSON_COUNT
                            state.people = peopleCount

                            const newArr = Array.from({
                                length: diff,
                            }).map(() => ({
                                ...emptyTicketBox,
                                boxId: uuidv4(),
                            }))
                            state.ticketBoxes = [...newArr]
                        } else {
                            state.people = FAMILY_PACKAGE_PERSON_COUNT
                            state.ticketBoxes = []
                        }
                    }
                } else {
                    if (state.people > FAMILY_PACKAGE_PERSON_COUNT) {
                        if (peopleCount) {
                            state.people = peopleCount

                            if (shouldKeepTickets) {
                                state.ticketBoxes = [...state.ticketBoxes]
                            } else {
                                const newArr = Array.from({
                                    length: peopleCount,
                                }).map(() => ({
                                    ...emptyTicketBox,
                                    boxId: uuidv4(),
                                }))
                                state.ticketBoxes = [...newArr]
                            }
                        } else {
                            state.people -= FAMILY_PACKAGE_PERSON_COUNT
                        }
                    } else {
                        if (peopleCount) {
                            state.people = peopleCount

                            if (shouldKeepTickets) {
                                state.ticketBoxes = [...state.ticketBoxes]
                            } else {
                                const newArr = Array.from({
                                    length: peopleCount,
                                }).map(() => ({
                                    ...emptyTicketBox,
                                    boxId: uuidv4(),
                                }))
                                state.ticketBoxes = newArr
                            }
                        } else {
                            state.people = 1
                        }
                    }
                }
            }

            state.familyPackage.isSelected = isSelected
        },
        setFamilyPackageVisibility(state, action: PayloadAction<boolean>) {
            state.familyPackage.isVisible = action.payload
        },
        setIsPackageConflictModalOpened(state, action: PayloadAction<boolean>) {
            state.familyPackage.isConflictModalOpened = action.payload
        },
        setIsSeniorConflictModalOpen(state, action: PayloadAction<boolean>) {
            state.seniorPackage.isConflictModalOpened = action.payload
        },
        removeFamilyPackage(state) {
            state.familyPackage = {
                isVisible: false,
                isSelected: false,
                isConflictModalOpened: false,
            }
        },
    },
})

export const {
    setMultipleDays,
    setDates,
    setIsCalendarOpened,
    removeDates,
    addPerson,
    removePerson,
    setTicketsVisible,
    addTicket,
    removeTicket,
    removeAllTickets,
    removeTicketTypes,
    updateTicketPersonType,
    updateTicketZoneType,
    setTicketsUpdating,
    updateTickets,
    setFamilyPackageSelection,
    removeTicketsKeepDate,
    setFamilyPackageVisibility,
    setIsPackageConflictModalOpened,
    removeFamilyPackage,
    setIsSeniorConflictModalOpen,
} = ticketSlice.actions
export default ticketSlice.reducer
